import React from 'react';
import { toFaDigits } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';

interface BlogPaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

/** شماره‌های نمایشی با «…» (مثل ۱ ۲ ۳ ۴ … ۸) */
const buildPages = (page: number, totalPages: number): Array<number | 'gap'> => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set<number>([1, totalPages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= totalPages - 2) [totalPages - 1, totalPages - 2, totalPages - 3].forEach((p) => pages.add(p));
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('gap');
    out.push(p);
  });
  return out;
};

/** صفحه‌بندی: دسکتاپ دکمه‌های ۴۰px، موبایل نوار فشردهٔ ۳۶px */
const BlogPagination: React.FC<BlogPaginationProps> = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  const items = buildPages(page, totalPages);
  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  return (
    <nav aria-label="صفحه‌بندی مقالات" className="pt-[18px] sm:pt-6">
      <div className="flex items-center justify-center gap-[6px] rounded-[12px] border border-[#e9e8e5] bg-white px-3 py-[10px] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] sm:gap-2 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:drop-shadow-none">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={prevDisabled}
          className={`flex h-9 items-center gap-1 rounded-[8px] px-[10px] text-[12px] font-medium leading-5 sm:h-auto sm:gap-[6px] sm:border sm:border-[#e9e8e5] sm:bg-white sm:px-4 sm:py-2 sm:text-[14px] ${
            prevDisabled ? 'cursor-not-allowed text-[rgba(91,95,102,0.4)] sm:text-[rgba(82,87,94,0.5)]' : 'text-[#331751] hover:bg-[#f4f3f1] sm:text-[#52575e]'
          }`}
        >
          <BlogIcon name="chevron-right-sm-muted" width={4.933} height={8} className="sm:hidden" />
          <BlogIcon name="chevron-right-muted" width={10.667} height={10.667} className="hidden sm:inline-block" />
          قبلی
        </button>

        <div className="flex items-center gap-1 sm:gap-2">
          {items.map((item, index) =>
            item === 'gap' ? (
              <span key={`gap-${index}`} className="flex h-9 w-6 items-center justify-center text-[12px] text-[rgba(91,95,102,0.6)] sm:h-10 sm:text-[14px] sm:text-[#52575e]">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onChange(item)}
                aria-current={item === page ? 'page' : undefined}
                className={`flex h-9 w-9 items-center justify-center rounded-[8px] text-[12px] leading-5 sm:h-10 sm:w-10 sm:text-[14px] ${
                  item === page
                    ? 'bg-[#4a2e68] font-semibold text-white drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] sm:bg-[#331751] sm:font-bold'
                    : 'font-medium text-[#5b5f66] hover:bg-[#f4f3f1] sm:border sm:border-[#e9e8e5] sm:bg-white sm:text-[#52575e]'
                }`}
              >
                {toFaDigits(item)}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={nextDisabled}
          className={`flex h-9 items-center gap-1 rounded-[8px] px-[10px] text-[12px] font-medium leading-5 sm:h-auto sm:gap-[6px] sm:border sm:border-[#e9e8e5] sm:bg-white sm:px-4 sm:py-2 sm:text-[14px] ${
            nextDisabled ? 'cursor-not-allowed text-[rgba(91,95,102,0.4)] sm:text-[rgba(82,87,94,0.5)]' : 'text-[#331751] hover:bg-[#f4f3f1] sm:text-[#52575e]'
          }`}
        >
          بعدی
          <BlogIcon name="chevron-left-sm-purple" width={4.933} height={8} className="sm:hidden" />
          <BlogIcon name="chevron-left" width={10.667} height={10.667} className="hidden sm:inline-block" />
        </button>
      </div>
    </nav>
  );
};

export default BlogPagination;
